import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Text, Heading } from "../../components";
import OTPInput from "react-otp-input";
import { API_DOMAIN } from "config";

export default function RegistrationPopup({ onClose, onSuccess }) {
  const [step, setStep] = useState("form"); // form | otp
  const [regType, setRegType] = useState("dealer"); // dealer | private

const [form, setForm] = useState({
  businessName: "",
  businessDisplayName: "", // ✅ חדש: שם בית עסק
  contactName: "",
  fullAddress: "",
  phone: "",
  email: "",
});


  const [errors, setErrors] = useState({});
  const [sid, setSid] = useState("");
  const [otp, setOtp] = useState("");
  const [msg, setMsg] = useState("");
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);

const dialogRef = useRef(null);
const otpWrapRef = useRef(null); // ✅ חדש: wrapper ל-OTP כדי למצוא את האינפוט הראשון

  const okPhone = (p) =>
    /^((\+972|972)|0)5\d{8}$/.test((p ?? "").replace(/-/g, "").trim());

  const okEmail = (e) => /^[\w-.]+@([\w-]+\.)+\w{2,}$/.test((e ?? "").trim());

  const fields = useMemo(() => {
    if (regType === "private") {
      return [
        { k: "contactName", label: "שם", type: "text", required: true },
        { k: "fullAddress", label: "כתובת מלאה", type: "text", required: true },
        { k: "phone", label: "טלפון נייד", type: "tel", required: true },
        { k: "email", label: "אימייל", type: "email", required: true },
      ];
    }

    return [
      { k: "businessName", label: "שם", type: "text", required: true },
      { k: "contactName", label: "שם איש קשר", type: "text", required: true },
      { k: "fullAddress", label: "כתובת מלאה", type: "text", required: true },
      { k: "phone", label: "טלפון נייד", type: "tel", required: true },
      { k: "email", label: "אימייל", type: "email", required: true },
    ];
  }, [regType]);

  const resetFlow = (t) => {
    setRegType(t);
    setErrors({});
    setMsg("");
    setStep("form");
    setOtp("");
    setSid("");
  };
useEffect(() => {
  if (step !== "otp") return;

  const t = setTimeout(() => {
    // 1) מייצב את הסקרול בתוך הפופאפ (עוזר כשמקלדת נפתחת)
    try {
      dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}

    // 2) פוקוס ידני לאינפוט הראשון של ה-OTP
    const first = otpWrapRef.current?.querySelector("input");
    first?.focus?.();
    first?.click?.(); // עוזר במובייל (בעיקר iOS) להציג מקלדת יציב
  }, 150);

  return () => clearTimeout(t);
}, [step]);

  const buildFd = () => {
    const fd = new FormData();

    const businessName = (form.businessName ?? "").trim();
    const contactName = (form.contactName ?? "").trim();
    const fullAddress = (form.fullAddress ?? "").trim();
    const phone = (form.phone ?? "").replace(/-/g, "").trim();
    const email = (form.email ?? "").trim();

    // ✅ מוכר פרטי נרשם כספק (2), סוחר כ-3
    const userRole = regType === "private" ? "2" : "3";
    const providerType = regType === "private" ? "private" : "company";

    fd.append("name", regType === "private" ? contactName : businessName);

    fd.append("business_name", businessName);
    fd.append("contact_name", contactName);
    fd.append("full_address", fullAddress);

    // backward compat keys
    fd.append("businessName", businessName);
    fd.append("contactName", contactName);
    fd.append("fullAddress", fullAddress);

    fd.append("phone", phone);
    fd.append("email", email);

    fd.append("user_role", userRole);
    fd.append("provider_type", providerType);

    return fd;
  };

  const validate = () => {
    const e = {};

    if (regType === "dealer") {
      if (!form.businessName?.trim()) e.businessName = "חובה שם משתמש";
      if (!form.contactName?.trim()) e.contactName = "חובה שם איש קשר";
      if (!form.fullAddress?.trim()) e.fullAddress = "חובה כתובת";
    } else {
      if (!form.contactName?.trim()) e.contactName = "חובה שם מלא";
      if (!form.fullAddress?.trim()) e.fullAddress = "חובה כתובת";
    }

    if (!okPhone(form.phone)) e.phone = "טלפון לא תקין";
    if (!okEmail(form.email)) e.email = "אימייל לא תקין";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: "" }));
    setMsg("");
  };

  const sendOtp = async () => {
    if (loadingSend) return;
    setMsg("");
    if (!validate()) return;

    setLoadingSend(true);
    try {
      const res = await fetch(`${API_DOMAIN}/otp/sendOtp`, {
        method: "POST",
        body: buildFd(),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok || !data) {
        setMsg("שגיאה בשליחת קוד");
        return;
      }

      if (!data.success) {
        if (data.code === "USER_EXISTS") setMsg("קיים משתמש עם מספר זה");
        else setMsg(data.message || "שגיאה בשליחת קוד");
        return;
      }

      setSid(data.session_id);
      setStep("otp");
      setMsg("");
      setOtp("");
    } finally {
      setLoadingSend(false);
    }
  };

  const verifyOtp = async () => {
    if (loadingVerify) return;
    if (otp.length !== 6) return;

    setLoadingVerify(true);
    setMsg("");
    try {
      const fd = buildFd();
      fd.append("otp", otp);
      fd.append("session_id", sid);

      const res = await fetch(`${API_DOMAIN}/otp/verifyOtp`, {
        method: "POST",
        body: fd,
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok || !data) {
        setMsg("שגיאה באימות הקוד");
        return;
      }

      if (!data.success) {
        setMsg(data.message || "קוד שגוי");
        return;
      }

      onSuccess?.(data);
      onClose?.();
    } finally {
      setLoadingVerify(false);
    }
  };
useEffect(() => {
  if (step !== "otp") return;
  if (otp.length !== 6) return;
  if (loadingVerify) return; // ✅ חדש: מונע אימות כפול

  const t = setTimeout(() => verifyOtp(), 200);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [otp, step, loadingVerify]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (step === "form") {
      setTimeout(() => {
        const el = dialogRef.current?.querySelector("input");
        el?.focus?.();
      }, 50);
    }
  }, [step, regType]);

  const subtitle =
    regType === "private"
      ? "הרשמה חינמית • מוגבל לרכב אחד בחודש"
      : "גישה לסוחר • אפשרויות מתקדמות";

  const title = regType === "private" ? "הרשמה כמוכר פרטי" : "הרשמה כסוחר";

  // ✅ Tabs בתוך הפופאפ: שורה עליונה נקייה + underline כמו Google, בתוך ה-card
  const TabBtn = ({ active, children, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={[
        "relative px-4 py-3 text-[14px] font-bold transition",
        active ? "text-red-a700_03" : "text-gray-600 hover:text-gray-900",
      ].join(" ")}
      aria-pressed={active}
    >
      {children}
      <span
        className={[
          "pointer-events-none absolute left-2 right-2 bottom-0 h-[3px] rounded-full transition",
          active ? "bg-red-a700_03" : "bg-transparent",
        ].join(" ")}
      />
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3"
      dir="rtl"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
<div
  dir="rtl"
  ref={dialogRef}
  style={{ backgroundColor: "white", height: "90vh" }}
  className="relative w-[min(92vw,520px)] rounded-[22px] bg-white
             shadow-[0_24px_60px_rgba(0,0,0,0.20)] ring-1 ring-black/5
             overflow-y-auto overflow-x-hidden"
  role="dialog"
  aria-modal="true"
>

        {/* ✅ TOP BAR: סגירה + טאבים (בתוך הפופאפ) */}
<div className="bg-white/95 border-b border-gray-200">
          {/* כפתור סגירה */}
          <button
            onClick={onClose}
            className="absolute left-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition"
            aria-label="סגירה"
            type="button"
          >
            ✕
          </button>

          {/* Tabs (רק בשלב form) */}
          {step === "form" ? (
            <div className="px-5 pt-3">
            {/*  <div className="flex items-center justify-center border-b border-gray-100">
                <TabBtn active={regType === "dealer"} onClick={() => resetFlow("dealer")}>
             סוחר
                </TabBtn>

                <div className="h-6 w-[1px] bg-gray-200" />

                <TabBtn active={regType === "private"} onClick={() => resetFlow("private")}>
                  מוכר פרטי
                </TabBtn>
              </div>*/}

              {/* כותרת + תת כותרת */}
              <div className="pt-4 pb-4 text-center">
                <Heading size="heading10xl" className="text-red-a700_03">
                  {title}
                </Heading>
                <Text className="mt-1 text-[13px] text-gray-700_04">{subtitle}</Text>
              </div>
            </div>
          ) : (
            // OTP header
            <div className="px-5 pt-5 pb-4 text-center">
              <Heading size="heading10xl" className="text-red-a700_03">
                אימות מספר
              </Heading>
              <Text className="mt-1 text-[13px] text-gray-700_04">
                הזינו את הקוד שנשלח אליכם
              </Text>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-5 pb-5 pt-4">
          {step === "form" && (
            <>
              <div className="space-y-4">
                {fields.map(({ k, label, placeholder, type, required }) => {
                  const hasErr = Boolean(errors[k]);

                  return (
                    <div key={k} className="text-right">
                      <div className="mb-1 flex items-center justify-between">
                        <Text className="text-[13px] text-gray-800 font-semibold">
                          {label}{" "}
                          {required && <span className="text-red-a700_03">*</span>}
                        </Text>
                      </div>

                      <div
                        className={[
                          "relative rounded-[14px] border bg-white transition",
                          hasErr ? "border-red-500" : "border-gray-200",
                          "hover:border-gray-300",
                          "focus-within:border-red-700 focus-within:ring-4 focus-within:ring-red-700/10",
                        ].join(" ")}
                      >
                        <input
                          value={form[k]}
                          onChange={(e) => handleChange(k, e.target.value)}
                          type={type}
                          placeholder={placeholder}
                          className={[
                            "w-full rounded-[14px]",
                            "h-[44px] px-3",
                            "bg-transparent text-[14px] text-gray-900 placeholder:text-gray-400",
                            "border-0 outline-none ring-0 shadow-none appearance-none",
                          ].join(" ")}
                        />
                      </div>

                      {hasErr && (
                        <Text className="mt-1 text-[12px] text-red-500">{errors[k]}</Text>
                      )}
                    </div>
                  );
                })}
              </div>

              {msg && (
                <div className="mt-3 rounded-[14px] border border-red-200 bg-red-50 px-3 py-2">
                  <Text className="text-center text-[13px] text-red-700">{msg}</Text>
                </div>
              )}

              <Button
                onClick={sendOtp}
                disabled={loadingSend}
                className={[
                  "mt-5 h-[46px] w-full rounded-[16px] bg-red-800 text-white",
                  "hover:bg-red-700 transition",
                  loadingSend ? "opacity-70 cursor-not-allowed" : "",
                ].join(" ")}
                style={{ color: "white" }}
              >
                {loadingSend ? "שולח..." : "קבל קוד"}
              </Button>
            </>
          )}

{step === "otp" && (
  <div
    className="min-h-[55vh] flex flex-col items-center justify-center"
    dir="ltr"
    style={{ direction: "ltr" }}
  >
    {/* אפשר להשאיר את הטקסט בעברית – רק את אזור הקוד להפוך ל־LTR */}
    <div dir="rtl" style={{ direction: "rtl" }}>
      <Text className="mb-3 text-center text-gray-700_04">
        הזינו את הקוד שנשלח אליכם
      </Text>
    </div>

    {/* ✅ ה־OTP עצמו ב־LTR + ref כדי לעשות פוקוס ידני */}
    <div ref={otpWrapRef} dir="ltr" style={{ direction: "ltr" }}>
      <OTPInput
        value={otp}
        onChange={(val) => {
          setOtp((val ?? "").replace(/\D/g, "").slice(0, 6));
          setMsg("");
        }}
        numInputs={6}
        // ✅ להסיר shouldAutoFocus (גורם קפיצות במובייל)
        // shouldAutoFocus

        renderSeparator={<span className="mx-1 text-gray-300"> </span>}
        renderInput={(inputProps, index) => (
          <input
            {...inputProps}
            key={index}

            // ✅ שיפור מובייל
            inputMode="numeric"
            pattern="\d*"
            enterKeyHint="done"

            // ✅ חשוב: הצעת OTP במכשירים (בעיקר iOS)
            autoComplete={index === 0 ? "one-time-code" : "off"}

            dir="ltr"
            style={{
              direction: "ltr",
              textAlign: "center",
              borderColor: "rgba(176,183,195,0.9)",
              fontSize: 18, // ✅ למנוע זום מוזר במובייל
            }}
            className={[
              "h-[52px] w-[48px] rounded-[14px] border text-center text-[18px]",
              "bg-gray-50 outline-none transition",
              "focus:border-red-700 focus:ring-4 focus:ring-red-700/10",
            ].join(" ")}
          />
        )}
      />
    </div>

    {msg && (
      <div
        className="mt-3 w-full rounded-[14px] border border-red-200 bg-red-50 px-3 py-2"
        dir="rtl"
      >
        <Text className="text-center text-[13px] text-red-700">{msg}</Text>
      </div>
    )}

    <Button
      onClick={verifyOtp}
      disabled={otp.length !== 6 || loadingVerify}
      className={[
        "mt-5 h-[46px] w-full rounded-[16px] bg-red-800 text-white",
        "hover:bg-red-700 transition",
        otp.length !== 6 || loadingVerify ? "opacity-50 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {loadingVerify ? "מאמת..." : "כניסה"}
    </Button>

    <div className="mt-4 flex w-full items-center justify-between gap-3" dir="rtl">
      <button
        type="button"
        onClick={() => !loadingSend && sendOtp()}
        className={[
          "text-[13px] text-gray-700_04 hover:underline",
          loadingSend ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        ].join(" ")}
      >
        לא קיבלתי קוד – שלחו שוב
      </button>

      <button
        type="button"
        onClick={() => {
          setStep("form");
          setOtp("");
          setMsg("");
        }}
        className="text-[13px] text-gray-700_04 hover:underline"
      >
        חזרה לפרטים
      </button>
    </div>
  </div>
)}


        </div>
      </div>
    </div>
  );
}
